import { makeStyles, tokens } from '@fluentui/react-components';
import { QualityIndicator } from '../common/QualityIndicator';
import { qualityWord } from '../../utils/interactionOutcome';
import type { QualityStatus } from '../../types';

/**
 * Call History quality, told two ways. A non-technical user gets one plain word
 * with a small coloured dot (spec §6: Healthy/Degraded/Poor/Unknown, and Unknown
 * is never dressed up as Healthy). A technical user keeps the existing icon
 * indicator and the jitter/latency tooltip.
 */
const TO_ASSESSMENT: Record<string, 'healthy' | 'degraded' | 'poor'> = {
  good: 'healthy',
  warning: 'degraded',
  poor: 'poor',
};

export function assessmentFor(rating: QualityStatus | null | undefined): 'healthy' | 'degraded' | 'poor' | 'unknown' {
  return TO_ASSESSMENT[rating ?? ''] ?? 'unknown';
}

const useStyles = makeStyles({
  wrap: { display: 'inline-flex', alignItems: 'center', gap: tokens.spacingHorizontalXS },
  dot: { width: '8px', height: '8px', borderRadius: tokens.borderRadiusCircular, flexShrink: 0 },
  healthy: { backgroundColor: tokens.colorPaletteGreenForeground1 },
  degraded: { backgroundColor: tokens.colorPaletteYellowForeground1 },
  poor: { backgroundColor: tokens.colorPaletteRedForeground1 },
  unknown: { backgroundColor: tokens.colorNeutralForeground4 },
});

export function QualityCell({ rating, technical, tooltip }: { rating: QualityStatus | null; technical: boolean; tooltip?: string }) {
  const styles = useStyles();
  const assessment = assessmentFor(rating);
  if (technical) {
    return (
      <span data-marker={assessment} title={tooltip}>
        <QualityIndicator status={rating ?? 'warning'} showLabel tooltip={tooltip} />
      </span>
    );
  }
  return (
    <span className={styles.wrap} data-marker={assessment}>
      <span className={`${styles.dot} ${styles[assessment]}`} aria-hidden="true" />
      {qualityWord(assessment)}
    </span>
  );
}
